import type { TurnEntry } from './turn-index.ts';
import type { ActivityState, RunActivity, ToolActivity } from './NarrationStore.ts';

export interface ActivityGroup {
  responseId: string;
  run?: RunActivity;
  request?: string;
  home: boolean;
  sharedResponse: boolean;
}

/** Project retained observations; the rail supplies labels, not a second activity history. */
export function activityGroups(
  runs: ReadonlyMap<string, RunActivity>,
  turns: readonly TurnEntry[],
  selected: { responseId?: string; request?: string; sharedResponse: boolean },
): ActivityGroup[] {
  const groups: ActivityGroup[] = [];
  const ids = [...runs.keys()];
  if (selected.responseId && !runs.has(selected.responseId)) {
    ids.unshift(selected.responseId);
  }
  for (const responseId of ids) {
    const matches = turns.filter((turn) => turn.responseId === responseId);
    const turn = matches.at(-1);
    const isSelected = responseId === selected.responseId;
    let request = isSelected ? selected.request : turn?.request;
    if (turn?.home) request = undefined;
    groups.push({
      responseId,
      run: runs.get(responseId),
      request,
      home: Boolean(turn?.home) && (!isSelected || selected.request === undefined),
      sharedResponse: isSelected ? selected.sharedResponse : matches.length > 1,
    });
  }
  return groups;
}

/** A returned Render alone cannot confirm that its update reached the canvas store. */
export function activityDisplayState(tool: ToolActivity): ActivityState {
  if (tool.kind === 'surface' && tool.state === 'returned' && !tool.surfaceApplied) {
    return 'unconfirmed';
  }
  return tool.state;
}

/** The rail resolves each turn to its last surface, not any intermediate artifact. */
export function activitySurfaceTurnIndex(turns: readonly TurnEntry[], surfaceId: string): number {
  return turns.findLastIndex((turn) => turn.surfaceIds.at(-1) === surfaceId);
}

export function activitySurfaceNavigationTargets(
  turns: readonly TurnEntry[],
  retainedIds: readonly string[],
  visibleId: string | null,
): string[] {
  return retainedIds.filter((id) => id !== visibleId && activitySurfaceTurnIndex(turns, id) >= 0);
}

/** Keep inspected request and operation facts on the same response, including rail detours. */
export function resolveActivityContext({
  turns,
  selectedTurn,
  viewingHistory,
  currentResponseId,
  pendingRequest,
}: {
  turns: readonly TurnEntry[];
  selectedTurn: TurnEntry | undefined;
  viewingHistory: boolean;
  currentResponseId: string | null;
  pendingRequest: string | null;
}): { responseId?: string; request?: string; sharedResponse: boolean } {
  if (!viewingHistory && pendingRequest !== null) {
    return { request: pendingRequest, sharedResponse: false };
  }
  let responseId = selectedTurn?.responseId;
  let turn = selectedTurn;
  if (!viewingHistory && currentResponseId) {
    responseId = currentResponseId;
    turn = turns.findLast((entry) => entry.responseId === responseId);
  }
  return {
    responseId,
    request: turn?.home ? undefined : turn?.request,
    sharedResponse:
      responseId !== undefined &&
      turns.filter((entry) => entry.responseId === responseId).length > 1,
  };
}
